/**
 * Utility functions for error handling
 */

const CAMERA_PHOTO_OPTIONS_ERROR_RE = /setPhotoOptions failed|setOptions failed/i;

/**
 * Chrome Android + ZXing throw this when torch/photo settings cannot be applied
 * (typically while starting or stopping the barcode camera). It is noisy and
 * should not surface as an app failure.
 */
export function isCameraPhotoOptionsError(error: any): boolean {
  if (error == null) {
    return false;
  }

  if (typeof error === 'string') {
    return CAMERA_PHOTO_OPTIONS_ERROR_RE.test(error);
  }

  if (typeof error !== 'object') {
    return false;
  }

  const candidate = error as {
    message?: any;
    name?: any;
    rejection?: any;
    reason?: any;
  };
  const parts = [candidate.message, candidate.name, String(error)]
    .filter(value => typeof value === 'string')
    .join(' ');

  if (CAMERA_PHOTO_OPTIONS_ERROR_RE.test(parts)) {
    return true;
  }

  if (candidate.rejection && candidate.rejection !== error) {
    return isCameraPhotoOptionsError(candidate.rejection);
  }

  if (candidate.reason && candidate.reason !== error) {
    return isCameraPhotoOptionsError(candidate.reason);
  }

  return false;
}

function collectFieldErrorMessages(errors: Record<string, any>): string[] {
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim()) {
          messages.push(item.trim());
        }
      }
    } else if (typeof value === 'string' && value.trim()) {
      messages.push(value.trim());
    }
  }
  return messages;
}

/**
 * Extracts a human-readable message from an HTTP error or API error body.
 * Prefers field validation errors (`errors.phone_number`, etc.), then `message`, then the fallback.
 */
/** API error code from an HTTP error body (e.g. `CARWASH_ALREADY_REDEEMED_TODAY`). */
export function extractErrorCode(error: any): string {
  const body = (error as { error?: { code?: any }; code?: any })?.error ?? error;
  return String((body as { code?: any })?.code ?? '').trim();
}

export function extractErrorMessage(error: any, fallback: string): string {
  const body = error?.error ?? error;

  if (body?.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
    const fieldMessages = collectFieldErrorMessages(body.errors as Record<string, any>);
    if (fieldMessages.length > 0) {
      return fieldMessages.join('. ');
    }
  }

  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message.trim();
  }

  const httpMessage = error?.message;
  if (
    typeof httpMessage === 'string' &&
    httpMessage.trim() &&
    !/^Http failure response/i.test(httpMessage)
  ) {
    return httpMessage.trim();
  }

  return fallback;
}

/**
 * Sanitizes error messages by checking for database error patterns
 * Hides technical database errors from users while preserving user-friendly messages
 * @param errorMessage - The error message to sanitize
 * @returns A sanitized error message safe to display to users
 */
export function sanitizeErrorMessage(errorMessage: string | undefined): string {
  if (!errorMessage) {
    return 'Something went wrong';
  }

  // Common database error patterns to detect
  const dbErrorPatterns = [
    // SQL keywords and error indicators
    /\b(SQL|sql|database|db|constraint|violation|foreign key|primary key|unique|duplicate entry|integrity)\b/i,
    // Error codes (e.g., ER_DUP_ENTRY, 23505, etc.)
    /\b(ER_|SQLSTATE|Error Code:|error code)\b/i,
    // Common database error formats with special characters
    /\(SQL|\(ER_|\(Error|\[SQL|\[Error|\{SQL|\{Error/i,
    // PostgreSQL/MySQL error patterns
    /\b(23505|23503|23502|42P01|42P07|duplicate key|violates foreign key|violates check constraint)\b/i,
    // Technical error patterns with underscores and brackets
    /[a-z_]+_[a-z_]+\(|\[[a-z_]+\]|\([a-z_]+\)/i,
  ];

  // Check if error matches any database error pattern
  const isDbError = dbErrorPatterns.some(pattern => pattern.test(errorMessage));

  // Also check for special characters that commonly appear in DB errors
  // but only if combined with technical-looking patterns
  const hasSpecialChars = /[({[_\-\]]/.test(errorMessage);
  const looksTechnical = /\b(error|exception|failed|invalid|constraint|violation|sql|db)\b/i.test(
    errorMessage
  );

  if (isDbError || (hasSpecialChars && looksTechnical)) {
    // Log the actual error for debugging purposes
    console.error('Database error (hidden from user):', errorMessage);
    return 'Something went wrong';
  }

  return errorMessage;
}
